import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import type { WebhookLog } from "@/api/webhook-log/_models/WebhookLog";
import { formatDateTime } from "@/app/date.utils";
import { WebhookLogListOutcomeChip } from "./WebhookLogListOutcomeChip";
import { WebhookLogListTemplate } from "./WebhookLogListTemplate";

interface WebhookLogListRowProps {
  isReplaying: boolean;
  onReplay: (webhookLog: WebhookLog) => void;
  webhookLog: WebhookLog;
}

const CELL_SX = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

/** One logged delivery: when, who, which event, what happened, and Replay. */
export const WebhookLogListRow = (props: WebhookLogListRowProps) => {
  const { isReplaying, onReplay, webhookLog } = props;

  let replayTitle = "Run this event through its handler again";
  if (!webhookLog.canReplay) replayTitle = "Only an authorised delivery with a JSON payload can be replayed";

  const handleReplay = () => {
    onReplay(webhookLog);
  };

  return (
    <WebhookLogListTemplate
      sx={{
        borderBottom: 1,
        borderColor: "divider",
        px: 1,
        py: 1,
      }}
    >
      <Typography
        sx={{ gridArea: "received" }}
        variant="body2"
      >
        {formatDateTime(webhookLog.receivedAt)}
      </Typography>
      <Typography
        sx={{
          color: "text.secondary",
          gridArea: "source",
        }}
        variant="body2"
      >
        {webhookLog.source}
      </Typography>
      <Typography
        sx={{
          ...CELL_SX,
          gridArea: "event",
        }}
        variant="body2"
      >
        {webhookLog.event ?? "—"}
        {webhookLog.replayOf && " ↻ replay"}
      </Typography>
      <Box sx={{ gridArea: "outcome" }}>
        <WebhookLogListOutcomeChip outcome={webhookLog.outcome} />
      </Box>
      <Tooltip title={webhookLog.error ?? ""}>
        <Typography
          sx={{
            ...CELL_SX,
            color: "error.main",
            gridArea: "error",
          }}
          variant="body2"
        >
          {webhookLog.error ?? ""}
        </Typography>
      </Tooltip>
      <Tooltip title={replayTitle}>
        <Box sx={{ gridArea: "replay" }}>
          <Button
            disabled={!webhookLog.canReplay || isReplaying}
            onClick={handleReplay}
            size="small"
            variant="outlined"
          >
            {isReplaying ? <CircularProgress size={16} /> : "Replay"}
          </Button>
        </Box>
      </Tooltip>
    </WebhookLogListTemplate>
  );
};
